import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Spin, Typography, theme } from 'antd';
import { IconArrowUp, IconHeadset } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import BackButton from '../../components/BackButton';
import EmptyState from '../../components/EmptyState';
import { message } from '../../services/toastMessage';
import {
  getMyAdminConversation,
  getMyAdminConversationMessages,
  sendMyAdminConversationMessage,
  startMyAdminConversation,
} from '../../services/AdminConversationService';
import { joinAdminConversation, leaveAdminConversation, onReceiveAdminMessage } from '../../services/RealtimeService';
import { IAdminConversationMessage, IAdminConversationSummary } from '../../interfaces/heavyDutyMachinery/IAdminConversation';
import { CORAL } from '../../theme/creamyTokens';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Text } = Typography;
const { TextArea } = Input;

// How close to the top (px) of the loaded messages triggers fetching the
// previous (older) page -- same threshold MessageThreadPanel uses.
const LOAD_OLDER_THRESHOLD_PX = 150;

/**
 * The signed-in user's own side of their AdminConversation (see that
 * entity's backend doc comment) -- unlike /heavy-duty-machinery/messages there's only
 * ever ONE thread here, so this is one simple page (list + input), not a
 * three-panel inbox: no booking status card, no cancel/view-address quick
 * actions, no typing indicator (HeavyDutyMachineryHub has no admin-chat equivalent of
 * SendTyping). Also this app's "Contactar con soporte" -- the input is never
 * disabled: with no conversation yet, or an already-Closed one, sending
 * calls StartOrReopen instead of the plain reply endpoint (see handleSend),
 * which creates/reopens it server-side. Reachable from the NotificationBell's
 * own AdminMessage link and from the always-present banner on
 * /heavy-duty-machinery/messages -- there's no separate persistent nav entry point.
 */
const Support: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [conversation, setConversation] = useState<IAdminConversationSummary | null | undefined>(undefined);
  const [messages, setMessages] = useState<IAdminConversationMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }));
  };

  useEffect(() => {
    setLoading(true);
    getMyAdminConversation()
      .then((summary) => {
        setConversation(summary);
        if (!summary) return undefined;
        return getMyAdminConversationMessages().then((page) => {
          setMessages(page.items);
          setHasMoreOlder(page.hasMore);
          scrollToBottom();
        });
      })
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_LOAD_ERROR)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOlder = async () => {
    if (loadingOlder || !hasMoreOlder || messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const cursor = Number(messages[0].id);
      const page = await getMyAdminConversationMessages(cursor);
      setMessages((prev) => [...page.items, ...prev]);
      setHasMoreOlder(page.hasMore);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_LOAD_ERROR));
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e.currentTarget.scrollTop < LOAD_OLDER_THRESHOLD_PX) loadOlder();
  };

  useEffect(() => {
    if (!conversation) return undefined;
    return onReceiveAdminMessage((incoming) => {
      if (incoming.conversationId !== conversation.id) return;
      setMessages((prev) => [...prev, incoming]);
      scrollToBottom();
    });
  }, [conversation]);

  // Same "tell the backend I'm looking at this right now" pattern as
  // MessageThreadPanel -- see HeavyDutyMachineryHub.JoinAdminConversation's own doc
  // comment for why this needs its own tracker rather than reusing the
  // booking-chat one.
  useEffect(() => {
    if (!conversation) return undefined;
    const conversationId = conversation.id;
    const syncActiveState = () => {
      if (document.visibilityState === 'visible') joinAdminConversation(conversationId);
      else leaveAdminConversation();
    };
    syncActiveState();
    document.addEventListener('visibilitychange', syncActiveState);
    return () => {
      document.removeEventListener('visibilitychange', syncActiveState);
      leaveAdminConversation();
    };
  }, [conversation]);

  const isOpen = conversation?.status === 'Open';

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      if (isOpen) {
        const sent = await sendMyAdminConversationMessage(trimmed);
        setMessages((prev) => [...prev, sent]);
        setText('');
        scrollToBottom();
        return;
      }
      // No conversation yet, or the existing one is Closed -- StartOrReopen
      // creates/reopens it server-side and saves this as its first/next
      // message; a plain fetch afterwards is simpler than hand-building a
      // IAdminConversationMessage from the summary response it returns.
      const updated = await startMyAdminConversation(trimmed);
      setConversation(updated);
      setText('');
      const page = await getMyAdminConversationMessages();
      setMessages(page.items);
      setHasMoreOlder(page.hasMore);
      scrollToBottom();
    } catch (error: any) {
      const translatedMessage =
        error.message === 'ADMIN_CONVERSATION_CLOSED'
          ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_CLOSED_NOTICE)
          : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_SEND_ERROR);
      message.error(translatedMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <AppLayout hideFooter>
      <div style={{ maxWidth: 640, width: '100%', margin: '0 auto', height: 'calc(100dvh - 128px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 16px 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* This page is only ever reached by drilling in (a notification,
              the Mensajes banner) -- never a header nav entry of its own --
              so it needs a way back same as any other detail page. */}
          <BackButton fallbackPath="/heavy-duty-machinery/messages" />
          <Text strong style={{ fontSize: 18 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_TITLE)}
          </Text>
        </div>

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                overscrollBehavior: 'contain',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 16px',
              }}
            >
              {loadingOlder && (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <Spin size="small" />
                </div>
              )}
              {/* No conversation yet -- an invitation to write the first
                  message below, not a dead end (see this page's own doc
                  comment: the input further down is never disabled). */}
              {messages.length === 0 && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <EmptyState
                    icon={<IconHeadset size={22} />}
                    title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_EMPTY_TITLE)}
                    description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_EMPTY_DESCRIPTION)}
                  />
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} style={{ alignSelf: m.isFromAdmin ? 'flex-start' : 'flex-end', maxWidth: '75%', minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2, textAlign: m.isFromAdmin ? 'left' : 'right' }}>
                    {translate(t, m.isFromAdmin ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_TEAM_LABEL : TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_YOU_LABEL)}
                  </Text>
                  <div
                    style={{
                      background: m.isFromAdmin ? token.colorFillTertiary : CORAL,
                      color: m.isFromAdmin ? token.colorText : '#fff',
                      borderRadius: 18,
                      padding: '8px 14px',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <div>{m.text}</div>
                    <Text style={{ fontSize: 10, opacity: 0.7, color: m.isFromAdmin ? token.colorTextSecondary : '#fff' }}>
                      {dayjs(m.sentAt).format('HH:mm')}
                    </Text>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <div style={{ flexShrink: 0, padding: '12px 16px', borderTop: `1px solid ${token.colorBorderSecondary}` }}>
              {/* Purely informational now -- writing while Closed reopens
                  the conversation (see handleSend), it never blocks. */}
              {conversation?.status === 'Closed' && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SUPPORT_CLOSED_NOTICE)}
                </Text>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <TextArea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_INPUT_PLACEHOLDER)}
                  autoSize={{ minRows: 1, maxRows: 5 }}
                  style={{ flex: 1, borderRadius: 20, resize: 'none', overflowWrap: 'anywhere' }}
                />
                <Button
                  type="primary"
                  shape="circle"
                  icon={<IconArrowUp size={18} />}
                  loading={sending}
                  disabled={!text.trim()}
                  onClick={handleSend}
                  style={{ width: 40, height: 40, minWidth: 40, flexShrink: 0 }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default Support;
